import React from 'react';
import { motion } from 'framer-motion';
import { Play, AlertCircle } from 'lucide-react';

export const Worlds = ({ eras, albums, onPlayAlbum }) => {
  const getAlbumsByEra = (eraId) => {
    const era = eras.find(e => e.id === eraId);
    if (!era) return [];
    return albums.filter(album => era.albumIds.includes(album.id));
  };

  if (!eras || eras.length === 0) return null;

  return (
    <section id="worlds" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-background via-charcoal to-background" />
      <div className="absolute inset-0 grain" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16 sm:mb-20"
        >
          <span className="text-[10px] font-body uppercase tracking-[0.4em] text-crimson mb-4 block">
            Musical Eras
          </span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl text-ivory">
            THE WORLDS
          </h2>
          <p className="mt-4 sm:mt-6 text-sm sm:text-base text-ivory/50 max-w-lg mx-auto font-body tracking-wide">
            Each era is a universe. Choose your entry point.
          </p>
        </motion.div>

        {/* Eras */}
        <div className="space-y-12 sm:space-y-16">
          {eras.map((era, index) => {
            const eraAlbums = getAlbumsByEra(era.id);
            const isEvenIndex = index % 2 === 0;
            
            return (
              <motion.div
                key={era.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className={`flex flex-col ${isEvenIndex ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-6 sm:gap-8 lg:gap-16 items-center`}
              >
                {/* Era Info */}
                <div className="lg:w-1/3 text-center lg:text-left">
                  <span className={`text-[10px] uppercase tracking-[0.3em] font-body ${
                    era.color === 'crimson' ? 'text-crimson' : 'text-neonBlue'
                  }`}>
                    {era.subtitle}
                  </span>
                  <h3 className="font-display text-4xl sm:text-5xl lg:text-6xl text-ivory mt-2 mb-3 sm:mb-4">
                    {era.name}
                  </h3>
                  <p className="text-sm sm:text-base text-ivory/50 font-body mb-4 sm:mb-6">
                    {era.description}
                  </p>
                </div>

                {/* Era Albums */}
                <div className="lg:w-2/3 w-full">
                  <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-4 scrollbar-hide">
                    {eraAlbums.map((album) => {
                      const hasPreview = album.tracks?.some(t => t.previewUrl);
                      
                      return (
                        <motion.div
                          key={album.id}
                          whileHover={{ y: -10 }}
                          className="relative shrink-0 w-40 sm:w-48 md:w-56 group cursor-pointer"
                          onClick={() => hasPreview && onPlayAlbum(album)}
                        >
                          <div className={`relative overflow-hidden border transition-all duration-400 ${
                            era.color === 'crimson' 
                              ? 'border-crimson/20 hover:border-crimson/50 hover:shadow-crimson' 
                              : 'border-neonBlue/20 hover:border-neonBlue/50'
                          }`}>
                            <div className="aspect-square overflow-hidden relative">
                              <img
                                src={album.coverArtUrl}
                                alt={album.title}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-600"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent opacity-80" />
                              
                              {/* Play overlay */}
                              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-background/40">
                                {hasPreview ? (
                                  <div className="w-12 h-12 rounded-full bg-crimson flex items-center justify-center shadow-neon">
                                    <Play className="w-5 h-5 text-primary-foreground ml-0.5" />
                                  </div>
                                ) : (
                                  <div className="flex flex-col items-center gap-1 text-ivory/70">
                                    <AlertCircle className="w-5 h-5" />
                                    <span className="text-[9px] uppercase tracking-wider">Soon</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
                              <p className="font-display text-sm sm:text-lg text-ivory">{album.title}</p>
                              <p className="text-[10px] sm:text-xs text-muted-foreground">{album.trackCount} Tracks</p>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
